import React, { useState, useEffect } from 'react';
import '../styles/PlacementOfficer.css';
import '../styles/PlacementRedirect.css'
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListCheck, faLink, faUsers, faEdit } from '@fortawesome/free-solid-svg-icons';
import EditPlacementModal from '../components/Placementdb/EditPlacementModal'; // Import the edit modal
import ProfileAvatarpp from '.././components/common/Avatar/ProfileAvatarpp';
import UserProfileDropdown from '../components/common/Avatar/UserProfileDropdown';


const Placementupcoming = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Pagination states - fixed records per page
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10; // Fixed value, no dropdown needed

  // Always visible fields (cannot be hidden)
  const alwaysVisibleFields = [
    'companyName',
    'placementOfficerName',
    'interviewDate',
    'qualification',
    'salary',
    'status',
    'directApplyLink',
    'skillKnown',
    'matchedStudent',
    'actions',
    'edit'
  ];
  // All available fields with their display names
  const allFields = [
    { key: 'actions', label: 'Actions', alwaysVisible: true },
    { key: 'sno', label: 'S.No', alwaysVisible: true },
    { key: 'placementOfficerName', label: 'Placement Officer Name', alwaysVisible: true },
    { key: 'technology', label: 'Technology', alwaysVisible: false },
    { key: 'skillSet', label: 'Skill Set', alwaysVisible: false },
    { key: 'skillKnown', label: 'Skills Known', alwaysVisible: true },
    { key: 'requirementIdentifiedDate', label: 'Requirement Identified Date', alwaysVisible: false },
    { key: 'companyName', label: 'Company Name', alwaysVisible: true },
    { key: 'companyLocation', label: 'Job Location', alwaysVisible: false },
    { key: 'recruitmentRole', label: 'Job Description', alwaysVisible: false },
    { key: 'contactPerson', label: 'Company Contact Person', alwaysVisible: false },
    { key: 'contactPersonNumber', label: 'Company Contact Number', alwaysVisible: false },
    { key: 'interviewDate', label: 'Interview Date', alwaysVisible: true },
    { key: 'experienceRequired', label: 'Experience Required', alwaysVisible: false },
    { key: 'qualification', label: 'Qualification', alwaysVisible: true },
    { key: 'passedOutYear', label: 'Passed Out Year', alwaysVisible: false },
    { key: 'bond', label: 'Bond', alwaysVisible: false },
    { key: 'email', label: 'Email', alwaysVisible: false },
    { key: 'Department', label: 'Department', alwaysVisible: false },
    { key: 'salary', label: 'Salary', alwaysVisible: true },
    { key: 'status', label: 'Status', alwaysVisible: true },
    { key: 'directApply', label: 'Direct Apply', alwaysVisible: false },
    { key: 'directApplyLink', label: 'Direct Apply Link', alwaysVisible: true },
    { key: 'recruitmentDetails', label: 'Additional Remarks', alwaysVisible: false }
  ];

  // State for visible fields
  const [visibleFields, setVisibleFields] = useState(() => {
    // Initialize with always visible fields and some default optional fields
    const defaultVisible = allFields.filter(field =>
      field.alwaysVisible || ['technology', 'experienceRequired'].includes(field.key)
    ).map(field => field.key);
    return defaultVisible;
  });

  useEffect(() => {
    fetchPlacementRecords();
  }, []);

  const fetchPlacementRecords = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get authentication token from localStorage
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');

      if (!token) {
        setError('Authentication token not found. Please login again.');
        setIsLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/placements/upcoming`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('Fetched placement records:', result);

        // Assuming the API returns data in result.data or directly in result
        const placementData = result.data || result || [];
        setRecords(placementData);
      } else {
        const responseText = await response.text();
        console.log('Error response:', responseText);

        let errorData;
        try {
          errorData = JSON.parse(responseText);
        } catch (e) {
          errorData = { message: responseText };
        }

        if (response.status === 401) {
          setError('Authentication failed. Please login again.');
        } else if (response.status === 404) {
          setError('No placement records found.');
        } else {
          setError(errorData.message || errorData.error || 'Failed to fetch placement records.');
        }
      }
    } catch (error) {
      console.error('Error fetching placement records:', error);

      if (error.name === 'TypeError') {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Function to check if interview date is in the past
  const isDateInPast = (dateString) => {
    if (!dateString) return false;
    const interviewDate = new Date(dateString);
    const currentDate = new Date();
    // Set time to start of day for accurate comparison
    currentDate.setHours(0, 0, 0, 0);
    interviewDate.setHours(0, 0, 0, 0);
    return interviewDate < currentDate;
  };

  // Updated Function to handle map button click - now passes the correct placement ID
  const handleMapClick = (record) => {
    console.log('Navigating to map with record:', record); // Debug log

    // Navigate to map page with company location data and placement ID
    navigate('/company-map', {
      state: {
        companyName: record.companyName,
        companyLocation: record.companyLocation,
        recordId: record.id || record.placementId // Use the correct ID field
      }
    });
  };

  // Function to handle matched data button click
  const handleMatchedDataClick = (record) => {
    console.log('Navigating to matched data with record:', record); // Debug log

    // Navigate to matched students page with placement record data
    navigate('/matched-students', {
      state: {
        companyName: record.companyName,
        qualification: record.qualification,
        skillKnown: record.skillKnown,
        salary: record.salary,
        experienceRequired: record.experienceRequired,
        recordId: record.id || record.placementId,
        dateOfPlacement: record.dateOfPlacement // Pass the entire record for reference
      }
    });
  };

  // Function to handle edit button click
  const handleEditClick = (record) => {
    console.log('Editing record:', record); // Debug log
    setSelectedRecord(record);
    setShowEditModal(true);
  };

  // Function to handle edit modal close
  const handleEditModalClose = () => {
    setShowEditModal(false);
    setSelectedRecord(null);
  };

 // In Placementupcoming.js, update the handleEditSave function:
const handleEditSave = (recordId, updatedFormData) => {
    // Update the record in the local state with all the new data
    setRecords(prevRecords =>
        prevRecords.map(record =>
            (record.id || record.placementId) === recordId
                ? { ...record, ...updatedFormData }
                : record
        )
    );
};

  // Function to handle configuration icon click
  const handleConfigClick = () => {
    setShowConfigModal(true);
  };

  // Function to close modal
  const handleCloseModal = () => {
    setShowConfigModal(false);
  };

  // Function to handle field visibility toggle
  const handleFieldToggle = (fieldKey) => {
    // Don't allow toggling of always visible fields
    if (alwaysVisibleFields.includes(fieldKey) || fieldKey === 'sno') {
      return;
    }

    setVisibleFields(prev => {
      if (prev.includes(fieldKey)) {
        return prev.filter(key => key !== fieldKey);
      } else {
        return [...prev, fieldKey];
      }
    });
  };

  // Function to handle select all / deselect all
  const handleSelectAll = () => {
    const requiredFields = allFields
      .filter(field => field.alwaysVisible || field.key === 'sno')
      .map(field => field.key);

    const allFieldKeys = allFields.map(field => field.key);

    // If all fields are selected, deselect all except required
    if (visibleFields.length === allFieldKeys.length) {
      setVisibleFields(requiredFields);
    } else {
      // Otherwise, select all fields
      setVisibleFields(allFieldKeys);
    }
  };

  // Check if all fields are selected
  const allFieldsSelected = visibleFields.length === allFields.length;

  // Function to get field value from record
  const getFieldValue = (record, fieldKey) => {
    switch (fieldKey) {
      case 'placementOfficerName':
        return record.placementOfficerName || record.placementOfficer || '-';
      case 'qualification':
        return record.qualification || record.Qualification || '-';
      case 'passedOutYear':
        return record.passedOutYear || record.Passedoutyear || '-';
      case 'interviewDate':
        return record.dateOfPlacement || '-';
      case 'directApplyLink':
        return record.directApplyLink ? (
          <a
            href={record.directApplyLink}
            target="_blank"
            rel="noopener noreferrer"
            className="apply-link"
          >
            Apply Now
          </a>
        ) : '-';

      case 'actions':
        return (
          <div className="actions-buttons">
            <button onClick={() => handleMatchedDataClick(record)} title="View Matched Students">
              <FontAwesomeIcon icon={faUsers} />
            </button>

            <button onClick={() => handleMapClick(record)} title="View Company Details & Matching Students">
              <FontAwesomeIcon icon={faLink} />
            </button>

            <button onClick={() => handleEditClick(record)} title="Edit Placement Record">
              <FontAwesomeIcon icon={faEdit} />
            </button>
          </div>
        );



      default:
        return record[fieldKey] || '-';
    }
  };

  // Pagination calculations
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = records.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(records.length / recordsPerPage);

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="placement-card">
          <div className="loading-state">
            <h3>Loading...</h3>
            <p>Fetching placement records...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="placement-card">
          <div className="error-state">
            <h3>Error</h3>
            <p>{error}</p>
            <div className="button-container">
              <button className="save-button" onClick={fetchPlacementRecords}>
                Try Again
              </button>
              <button
                className="backs-button"
                onClick={() => navigate('/placementofficer')}
              >
                Back to Form
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <ProfileAvatarpp />
      {records.length > 0 ? (
        <div className="placement-table-wrapper">
          <div className="placement-table-header">
            <h2>Company Records</h2>
            <div className="user-dropdown">
              <UserProfileDropdown />
            </div>
          </div>

          <div className="placement-table-container">
            <div className="table-scroll-wrapper">
              <table className="placement-table">
                <thead>
                  <tr>
                    {allFields.filter(field => visibleFields.includes(field.key)).map(field => (
                      <th key={field.key}>{field.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentRecords.map((record, index) => (
                    <tr
                      key={record.id || index}
                      className={isDateInPast(record.dateOfPlacement) ? 'past-date-row' : ''}
                    >
                      {allFields.filter(field => visibleFields.includes(field.key)).map(field => (
                        <td key={field.key} className={
                          field.key === 'recruitmentRole' ? 'job-description-cell' :
                            field.key === 'directApplyLink' ? 'link-cell' :
                              field.key === 'recruitmentDetails' ? 'remarks-cell' :
                                field.key === 'actions' ? 'actions-cell' :
                                  field.key === 'matchedStudent' ? 'matched-student-cell' :
                                    field.key === 'edit' ? 'edit-cell' : ''
                        }>
                          {field.key === 'sno'
                            ? indexOfFirstRecord + index + 1
                            : getFieldValue(record, field.key)
                          }
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination with Configuration Icon */}
          <div className="pagination-wrapper">
            <div className="pagination-container">
              <button
                onClick={handlePrevious}
                disabled={currentPage === 1}
                className="paginationsr-btn"
              >
                «
              </button>

              {[...Array(totalPages)].map((_, index) => {
                const pageNumber = index + 1;
                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={`paginationsr-btn ${currentPage === pageNumber ? 'active' : ''}`}
                  >
                    {pageNumber}
                  </button>
                );
              })}

              <button
                onClick={handleNext}
                disabled={currentPage === totalPages}
                className="paginationsr-btn"
              >
                »
              </button>
            </div>

            {/* Configuration Icon in the same row */}
            <div className="config-icon-container">
              <button
                className="config-icon"
                onClick={handleConfigClick}
                title="Configure Table Fields"
              >
                <FontAwesomeIcon icon={faListCheck} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="placement-card">
          <div className="no-records">
            <h3>No Records Found</h3>
            <p>No placement records have been saved yet.</p>
            <div className="button-container">
              <button
                className="save-button"
                onClick={() => navigate('/placementofficer')}
              >
                Add New Record
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Modal */}
      {showConfigModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Configure Table Fields</h3>
              <button className="modal-close-btn" onClick={handleCloseModal}>
                x
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Select the fields you want to display in the table.
                <strong> Required fields cannot be hidden.</strong>
              </p>

              <div className="fields-list">
                {allFields.map(field => (
                  <div key={field.key} className="field-item">
                    <label className="field-checkbox">
                      <input
                        type="checkbox"
                        checked={visibleFields.includes(field.key)}
                        onChange={() => handleFieldToggle(field.key)}
                        disabled={field.alwaysVisible || field.key === 'sno'}
                      />
                      <span className="checkmark"></span>
                      <span className="field-label">
                        {field.label}
                        {(field.alwaysVisible || field.key === 'sno') &&
                          <span className="required-badge">Required</span>
                        }
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <div className="select-all-container">
                <button className="select-all-btn" onClick={handleSelectAll}>
                  {allFieldsSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <button className="modal-btn modal-btn-secondary" onClick={handleCloseModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Placement Modal */}
      <EditPlacementModal
        isOpen={showEditModal}
        onClose={handleEditModalClose}
        record={selectedRecord}
        onSave={handleEditSave}
      />
    </div>
  );
};

export default Placementupcoming;